/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type ChangeEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Content, Header, Link, Page } from '@backstage/core-components';
import {
  HeaderWorldClock,
  WelcomeTitle,
  type ClockConfig,
} from '@backstage/plugin-home';
import SearchIcon from '@material-ui/icons/Search';
import { TenantSection } from './TenantSection';

const clockConfigs: ClockConfig[] = [
  {
    label: 'NYC',
    timeZone: 'America/New_York',
  },
  {
    label: 'UTC',
    timeZone: 'UTC',
  },
  {
    label: 'STO',
    timeZone: 'Europe/Stockholm',
  },
  {
    label: 'TYO',
    timeZone: 'Asia/Tokyo',
  },
];

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;
const MAX_RESULTS = 6;

type SearchResultItem = {
  type: string;
  document: {
    title: string;
    text: string;
    location: string;
  };
  highlight?: {
    preTag: string;
    postTag: string;
    fields: {
      title?: string;
      text?: string;
    };
  };
};

function renderHighlighted(value: string, preTag: string, postTag: string): ReactNode {
  if (!preTag || !value.includes(preTag)) {
    return value;
  }

  const segments = value.split(preTag);

  return segments.map((segment, index) => {
    if (index === 0) {
      return segment;
    }

    const [highlighted, ...rest] = segment.split(postTag);

    return (
      <span key={index}>
        <mark className="rdap-home-hero-search-mark">{highlighted}</mark>
        {rest.join(postTag)}
      </span>
    );
  });
}

function HeroSearch() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const trimmed = query.trim();

    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setIsOpen(false);
      setIsLoading(false);
      return undefined;
    }

    let isCancelled = false;
    setIsLoading(true);

    const timeoutId = window.setTimeout(async () => {
      try {
        const searchBaseUrl = await discoveryApi.getBaseUrl('search');
        const response = await fetchApi.fetch(
          `${searchBaseUrl}/query?${new URLSearchParams({
            term: trimmed,
          }).toString()}`,
        );

        if (!response.ok) {
          throw new Error(`Search request failed (${response.status})`);
        }

        const data = (await response.json()) as {
          results?: SearchResultItem[];
        };

        if (!isCancelled) {
          setResults(data.results ?? []);
          setIsOpen(true);
        }
      } catch {
        if (!isCancelled) {
          setResults([]);
          setIsOpen(true);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [query, discoveryApi, fetchApi]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  const hasQuery = query.trim().length > 0;
  const trimmedQuery = query.trim();

  let resultsContent: ReactNode;
  if (isLoading) {
    resultsContent = (
      <p className="rdap-home-hero-search-status">Ricerca in corso...</p>
    );
  } else if (results.length === 0) {
    resultsContent = (
      <p className="rdap-home-hero-search-status">
        Nessun risultato per &quot;{trimmedQuery}&quot;.
      </p>
    );
  } else {
    resultsContent = results.slice(0, MAX_RESULTS).map((result, index) => {
      const preTag = result.highlight?.preTag ?? '';
      const postTag = result.highlight?.postTag ?? '';
      const title = result.highlight?.fields.title
        ? renderHighlighted(result.highlight.fields.title, preTag, postTag)
        : result.document.title;
      const text = result.highlight?.fields.text
        ? renderHighlighted(result.highlight.fields.text, preTag, postTag)
        : result.document.text;

      return (
        <Link
          key={`${result.document.location}-${index}`}
          to={result.document.location}
          className="rdap-home-hero-search-result"
          underline="none"
        >
          <span className="rdap-home-hero-search-result-title">{title}</span>
          <span className="rdap-home-hero-search-result-text">{text}</span>
        </Link>
      );
    });
  }

  return (
    <div className="rdap-home-hero-search" ref={containerRef}>
      <form
        className="rdap-home-hero-search-form"
        role="search"
        onSubmit={event => event.preventDefault()}
      >
        <input
          type="search"
          className="rdap-home-hero-search-input"
          placeholder="Search systems, components, documentation..."
          aria-label="Search the catalog"
          value={query}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setIsOpen(true)}
        />
        {!hasQuery ? (
          <span className="rdap-home-hero-search-icon" aria-hidden="true">
            <SearchIcon fontSize="small" />
          </span>
        ) : null}
      </form>

      {isOpen ? (
        <div className="rdap-home-hero-search-results">{resultsContent}</div>
      ) : null}
    </div>
  );
}

function HomeHero() {
  return (
    <section className="rdap-home-hero" aria-label="R-DAP Sense">
      <div className="rdap-home-hero-brand">
        <img
          className="rdap-home-hero-icon"
          src="/chatbot-sense.png"
          alt=""
          aria-hidden="true"
        />
        <h1 className="rdap-home-hero-title">
          <span className="rdap-home-hero-title-accent">R-DAP</span> Sense
        </h1>
      </div>
      <p className="rdap-home-hero-subtitle">
        Work it, make it, Do it makes us: Harder, better, Faster, stronger
      </p>
      <HeroSearch />
    </section>
  );
}

export function CustomHomePageLayout() {
  return (
    <Page themeId="home">
      <Header title={<WelcomeTitle />} pageTitleOverride="Home">
        <HeaderWorldClock clockConfigs={clockConfigs} />
      </Header>
      <Content>
        <HomeHero />

        <hr className="rdap-home-divider" />

        <TenantSection />

      </Content>
    </Page>
  );
}
